import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AccessTime, CloseOutlined, SubjectOutlined } from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import dayjs, { Dayjs } from 'dayjs';
import clsx from 'clsx';
import { useRecoilState } from 'recoil';
import { DefaultEventCategoryId } from '@demind-inc/core';

import './TemporaryEventMenu.scss';
import {
  assistantChatProcessedIdsAtom,
  ChatSuggestedEvent,
  circadianPhaseInfo,
  useAssistantChat,
  useCalendarContext,
  useCircadianContext,
  useEventDetailsMenuContext,
} from '../../../../data-access';
import { aiScheduledActionsAtom } from '../../../../data-access/recoil';
import { EventDescription } from '../EventDescription';
import { useGeneralSettings } from '../../../../hooks/useGeneralSettings';
import { useAllPlanSuggestedModal } from '../../../../hooks';
import { useAIScheduler } from '../../../../hooks/useAIScheduler';
import { useChatCTAHandler } from '../../../../hooks/useChatCTAHandler';
import {
  formatTaskTimeRange,
  getAiScheduledActionStableId,
  isAssistantChatTemporalSuggestedCalendarEvent,
  isTemporaryEventMenuSchedulerTimeEditable,
  translateAIScheduledActionToTemporalCalendarEvent,
} from '../../../../helpers';
import { CALENDAR_STEP_MIN } from '../../CalendarTimeline/constants';
import { CustomTimeSelector } from '../../../common/CustomTimeSelector';
import { useTranslation } from 'react-i18next';
import {
  formatDataDate,
  getCircadianPhaseTitle,
  getDefaultEventCategoryTitle,
} from '../../../DataView/i18n';

interface TemporaryEventMenuProps {
  visible: boolean;
  onClose: () => void;
}

const LIFESTACK_FOOTER_REGEX = /\[Created from Lifestack \(https:\/\/lifestack\.ai\)\]\.?/gi;

const TemporaryEventMenu: React.FC<TemporaryEventMenuProps> = ({ visible, onClose }) => {
  const { t, i18n } = useTranslation();
  const { selectedEvent, selectedEventAnchorRect, patchSelectedTemporaryEventPreview } =
    useEventDetailsMenuContext();
  const { mainCalendar, findCalendarItem } = useCalendarContext();
  const { findPhaseForTaskTime } = useCircadianContext();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const [aiScheduledActions, setAiScheduledActions] = useRecoilState(aiScheduledActionsAtom);
  const { acceptSchedule, discardSchedule, updateScheduleTime } = useAllPlanSuggestedModal();
  const { handleAcceptAIScheduleEvent, handleDiscardEvent } = useAIScheduler();
  const { temporalSuggestedEvents, removeTemporalSuggestedEvent, setTemporalSuggestedEvents } =
    useAssistantChat();
  const [, setProcessedIds] = useRecoilState(assistantChatProcessedIdsAtom);
  const { handleCTAEvent } = useChatCTAHandler({
    removeTemporalSuggestedEvent,
    setTemporalSuggestedEvents,
  });

  const [timeAnchorEl, setTimeAnchorEl] = useState<HTMLElement | null>(null);
  const [draftStart, setDraftStart] = useState<Dayjs>(() => dayjs());
  const [draftEnd, setDraftEnd] = useState<Dayjs>(() => dayjs());
  const [menuPaperHeight, setMenuPaperHeight] = useState<number>(0);
  const [isAccepting, setIsAccepting] = useState(false);

  const { displayCalendar } = useMemo(() => {
    if (!selectedEvent) {
      return { displayCalendar: undefined };
    }
    const calId = selectedEvent.calendarId;
    const fromId = calId ? findCalendarItem(calId) : undefined;
    const displayCalendar = fromId ?? mainCalendar;
    return { displayCalendar };
  }, [selectedEvent, findCalendarItem, mainCalendar]);

  const selectedCategory = selectedEvent?.categories?.[0] ?? null;

  const canEditSchedulerTime = useMemo(
    () => (selectedEvent ? isTemporaryEventMenuSchedulerTimeEditable(selectedEvent) : false),
    [selectedEvent]
  );
  const allPlanSuggestionId = useMemo(() => {
    if (!selectedEvent?.eventId?.startsWith('all_plan_')) {
      return null;
    }
    return selectedEvent.eventId.replace('all_plan_', '');
  }, [selectedEvent]);
  const aiSchedulerSuggestionId = selectedEvent?.autoScheduleFrom?.id ?? null;
  const chatSuggestion = useMemo<ChatSuggestedEvent | null>(() => {
    if (!selectedEvent?.eventId) {
      return null;
    }
    return temporalSuggestedEvents.find((event) => event.eventId === selectedEvent.eventId) ?? null;
  }, [selectedEvent?.eventId, temporalSuggestedEvents]);
  const isAssistantChatSuggestion = useMemo(
    () => !!selectedEvent && isAssistantChatTemporalSuggestedCalendarEvent(selectedEvent),
    [selectedEvent]
  );
  const actionId = allPlanSuggestionId ?? aiSchedulerSuggestionId;
  const shouldShowActionButtons = Boolean(
    actionId || (isAssistantChatSuggestion && chatSuggestion)
  );

  const startDate = useMemo(
    () => (selectedEvent ? dayjs(selectedEvent.start) : dayjs()),
    [selectedEvent]
  );
  const endDate = useMemo(
    () => (selectedEvent ? dayjs(selectedEvent.end) : dayjs()),
    [selectedEvent]
  );
  const isAllDay = !!selectedEvent?.allDay;

  const taskTime = useMemo(() => {
    if (!selectedEvent || isAllDay) {
      return '';
    }
    return formatTaskTimeRange({
      startDatetime: startDate,
      endDatetime: endDate,
      timeFormat,
    });
  }, [selectedEvent, isAllDay, startDate, endDate, timeFormat]);

  const targetPhase = findPhaseForTaskTime(
    startDate.format('YYYY-MM-DD HH:mm'),
    endDate.format('YYYY-MM-DD HH:mm')
  );
  const phase = circadianPhaseInfo[targetPhase];

  const allDayLabel = useMemo(() => {
    if (!selectedEvent || !isAllDay) {
      return '';
    }
    return t('calendar.date.allDayDate', {
      date: formatDataDate(i18n.language, startDate.toDate(), {
        month: 'short',
        day: 'numeric',
      }),
    });
  }, [selectedEvent, isAllDay, startDate, i18n.language, t]);

  useEffect(() => {
    if (!visible) {
      setTimeAnchorEl(null);
      setIsAccepting(false);
    }
  }, [visible]);

  const paperPosition = useMemo(() => {
    if (!selectedEventAnchorRect || typeof window === 'undefined') {
      return undefined;
    }
    const menuWidth = 390;
    const menuOffset = 12;
    const viewportPadding = 12;
    const viewportHeight = window.innerHeight;
    const effectiveMenuHeight = menuPaperHeight > 0 ? menuPaperHeight : 320;
    const preferredLeft = selectedEventAnchorRect.right + menuOffset;
    const fallbackLeft = selectedEventAnchorRect.left - menuWidth - menuOffset;
    const left =
      preferredLeft + menuWidth + viewportPadding <= window.innerWidth
        ? preferredLeft
        : Math.max(viewportPadding, fallbackLeft);
    const maxTop = Math.max(
      viewportPadding,
      viewportHeight - viewportPadding - effectiveMenuHeight
    );
    const top = Math.min(Math.max(viewportPadding, selectedEventAnchorRect.top), maxTop);
    return { top, left };
  }, [selectedEventAnchorRect, menuPaperHeight]);

  const handlePaperRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      return;
    }
    const nextHeight = node.getBoundingClientRect().height;
    setMenuPaperHeight((prev) => (Math.abs(prev - nextHeight) > 1 ? nextHeight : prev));
  }, []);

  const openTimePopover = useCallback(
    (anchor: HTMLElement) => {
      if (!selectedEvent) {
        return;
      }
      setTimeAnchorEl(anchor);
      setDraftStart(dayjs(selectedEvent.start));
      setDraftEnd(dayjs(selectedEvent.end));
    },
    [selectedEvent]
  );

  const handleApplyTimeEdit = useCallback(() => {
    if (!selectedEvent || !canEditSchedulerTime) {
      return;
    }
    let start = draftStart.toDate();
    let end = draftEnd.toDate();
    if (dayjs(start).isSameOrAfter(dayjs(end))) {
      end = dayjs(start).add(CALENDAR_STEP_MIN, 'minute').toDate();
      setDraftEnd(dayjs(end));
    }

    if (allPlanSuggestionId) {
      updateScheduleTime(allPlanSuggestionId, dayjs(start).toISOString(), dayjs(end).toISOString());
      patchSelectedTemporaryEventPreview({ start, end });
      setTimeAnchorEl(null);
      return;
    }

    const stableId = selectedEvent.autoScheduleFrom?.id;
    if (!stableId) {
      return;
    }
    const actionIdx = aiScheduledActions.findIndex(
      (a) => getAiScheduledActionStableId(a) === stableId
    );
    if (actionIdx === -1) {
      return;
    }
    const nextActions = [...aiScheduledActions];
    nextActions[actionIdx] = {
      ...nextActions[actionIdx],
      startDate: dayjs(start).toISOString(),
      endDate: dayjs(end).toISOString(),
    };
    setAiScheduledActions(nextActions);
    const updatedAction = nextActions[actionIdx];
    const cal = translateAIScheduledActionToTemporalCalendarEvent(updatedAction);
    patchSelectedTemporaryEventPreview({
      start: new Date(cal.start.date),
      end: new Date(cal.end.date),
      eventId: cal.eventId,
      autoScheduleFromId: getAiScheduledActionStableId(updatedAction),
    });

    setTimeAnchorEl(null);
  }, [
    allPlanSuggestionId,
    selectedEvent,
    canEditSchedulerTime,
    draftStart,
    draftEnd,
    aiScheduledActions,
    setAiScheduledActions,
    patchSelectedTemporaryEventPreview,
    updateScheduleTime,
  ]);

  if (!selectedEvent) {
    return null;
  }

  const amPm = timeFormat === '12h';
  const isStartAfterEnd = draftStart.isSameOrAfter(draftEnd);
  const eventAccentColor =
    selectedEvent.color || selectedEvent.calendarColor || 'var(--color-primary, #680ddb)';
  const cleanedDescription = (selectedEvent.description || '')
    .replace(LIFESTACK_FOOTER_REGEX, '')
    .trim();
  const hasDescription = cleanedDescription.length > 0;

  return (
    <Dialog
      open={visible}
      className={clsx('temporary-event-menu', {
        'temporary-event-menu--anchored': !!paperPosition,
      })}
      onClose={onClose}
      disableRestoreFocus
      onKeyDown={(e) => e.stopPropagation()}
      PaperProps={
        paperPosition
          ? {
              style: {
                position: 'fixed',
                top: paperPosition.top,
                left: paperPosition.left,
                right: 'auto',
                bottom: 'auto',
                margin: 0,
                maxHeight: 'min(85vh, calc(100vh - 24px))',
              },
              ref: handlePaperRef,
            }
          : undefined
      }
    >
      <div className="temporary-event-menu__color-bar" style={{ background: eventAccentColor }} />
      <DialogTitle className="temporary-event-menu__header">
        {selectedCategory ? (
          <div
            className="temporary-event-menu__header__category-chip"
            style={{ backgroundColor: `${eventAccentColor}1f`, color: eventAccentColor }}
          >
            {getDefaultEventCategoryTitle(
              t,
              selectedCategory.id as DefaultEventCategoryId,
              selectedCategory.name
            )}
          </div>
        ) : (
          <div className="temporary-event-menu__header__category-chip temporary-event-menu__header__category-chip--empty">
            {t('calendar.common.suggested')}
          </div>
        )}
        <div className="temporary-event-menu__header__actions">
          <CloseOutlined onClick={onClose} className="temporary-event-menu__header__icon" />
        </div>
      </DialogTitle>
      <DialogContent className="temporary-event-menu__content">
        <div className="temporary-event-menu__title-container">
          <h2 className="temporary-event-menu__title">
            {selectedEvent.title || t('calendar.common.untitled')}
          </h2>
        </div>
        {(taskTime || allDayLabel) && (
          <div className="temporary-event-menu__phase-container">
            {(taskTime || allDayLabel) && (
              <div className="temporary-event-menu__phase-container__time">
                <AccessTime className="temporary-event-menu__phase-container__time__icon" />
                {taskTime || allDayLabel}
                {canEditSchedulerTime && (
                  <IconButton
                    type="button"
                    size="small"
                    className="temporary-event-menu__phase-container__time-edit"
                    aria-label={t('calendar.eventMenu.editTime')}
                    onClick={(e) => openTimePopover(e.currentTarget)}
                  >
                    <EditOutlinedIcon fontSize="inherit" />
                  </IconButton>
                )}
              </div>
            )}
            {phase?.label && !isAllDay && (
              <div
                className="temporary-event-menu__phase-container__phase"
                style={{ backgroundColor: `${phase.color}1f`, color: phase.color }}
              >
                <span
                  className="temporary-event-menu__phase-container__phase__dot"
                  style={{ backgroundColor: phase.color }}
                />
                {getCircadianPhaseTitle(t, targetPhase, phase.label)}
              </div>
            )}
          </div>
        )}
        <Popover
          open={Boolean(timeAnchorEl)}
          anchorEl={timeAnchorEl}
          onClose={() => setTimeAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: { className: 'temporary-event-menu__time-popover-paper' },
          }}
        >
          <div className="temporary-event-menu__time-popover">
            <Typography
              variant="subtitle2"
              component="p"
              className="temporary-event-menu__time-popover-title"
            >
              {t('calendar.eventMenu.adjustTime')}
            </Typography>
            <div className="temporary-event-menu__time-fields">
              <div className="temporary-event-menu__time-field">
                <Typography className="temporary-event-menu__time-field-label">
                  {t('calendar.common.start')}
                </Typography>
                <CustomTimeSelector
                  className="temporary-event-menu__time-selector"
                  value={draftStart}
                  onChange={(v) => v && setDraftStart(v)}
                  amPm={amPm}
                  interval={CALENDAR_STEP_MIN}
                  size="small"
                />
              </div>
              <div className="temporary-event-menu__time-field">
                <Typography className="temporary-event-menu__time-field-label">
                  {t('calendar.common.end')}
                </Typography>
                <CustomTimeSelector
                  className="temporary-event-menu__time-selector"
                  value={draftEnd}
                  onChange={(v) => v && setDraftEnd(v)}
                  amPm={amPm}
                  interval={CALENDAR_STEP_MIN}
                  size="small"
                />
              </div>
            </div>
            <Button
              variant="contained"
              size="small"
              fullWidth
              className="temporary-event-menu__time-apply-btn"
              disabled={isStartAfterEnd}
              onClick={() => handleApplyTimeEdit()}
            >
              {t('calendar.common.apply')}
            </Button>
          </div>
        </Popover>
        {displayCalendar && (
          <div
            className={clsx(
              'temporary-event-menu__content__item',
              'temporary-event-menu__content__item--static'
            )}
          >
            <AccountCircleIcon
              className="temporary-event-menu__content__item__icon"
              fontSize="small"
            />
            <div
              className={clsx(
                'temporary-event-menu__content__item__text',
                'temporary-event-menu__name-text'
              )}
            >
              {displayCalendar.name}
              {displayCalendar.calendarType === 'outlook' && displayCalendar.rootEmail
                ? ` (${displayCalendar.rootEmail})`
                : ''}
            </div>
          </div>
        )}
        {hasDescription && (
          <div className="temporary-event-menu__notes-section">
            <div className="temporary-event-menu__notes-section__label">
              <SubjectOutlined className="temporary-event-menu__notes-section__label__icon" />
              {t('calendar.common.notes')}
            </div>
            <EventDescription
              description={cleanedDescription}
              onChangeDescription={() => {}}
              disabled
              hideToolbar
              className={clsx(
                'temporary-event-menu__content__item',
                'temporary-event-menu__content__item--description'
              )}
            />
          </div>
        )}
      </DialogContent>
      {shouldShowActionButtons && (
        <DialogActions disableSpacing className="temporary-event-menu__actions">
          <div className="temporary-event-menu__button-group">
            <Button
              onClick={async () => {
                if (chatSuggestion) {
                  removeTemporalSuggestedEvent(chatSuggestion.eventId);
                  setProcessedIds((prev) =>
                    prev.includes(chatSuggestion.eventId) ? prev : [...prev, chatSuggestion.eventId]
                  );
                  onClose();
                  return;
                }
                if (allPlanSuggestionId) {
                  discardSchedule(allPlanSuggestionId);
                } else if (aiSchedulerSuggestionId) {
                  handleDiscardEvent(aiSchedulerSuggestionId);
                }
                onClose();
              }}
              variant="text"
              disabled={isAccepting}
              className="temporary-event-menu__button-group__button temporary-event-menu__button-group__button--cancel"
            >
              {t('calendar.common.discard')}
            </Button>
            <LoadingButton
              variant="contained"
              loading={isAccepting}
              disabled={isAccepting}
              onClick={async () => {
                setIsAccepting(true);
                try {
                  if (chatSuggestion) {
                    const accepted = await handleCTAEvent(chatSuggestion);
                    if (accepted) {
                      setProcessedIds((prev) =>
                        prev.includes(chatSuggestion.eventId)
                          ? prev
                          : [...prev, chatSuggestion.eventId]
                      );
                      onClose();
                    }
                    return;
                  }
                  if (allPlanSuggestionId) {
                    await acceptSchedule(allPlanSuggestionId);
                  } else if (aiSchedulerSuggestionId) {
                    const sourceAction = aiScheduledActions.find(
                      (action) => getAiScheduledActionStableId(action) === aiSchedulerSuggestionId
                    );
                    if (sourceAction) {
                      const calendarEvent =
                        translateAIScheduledActionToTemporalCalendarEvent(sourceAction);
                      await handleAcceptAIScheduleEvent(calendarEvent);
                    }
                  }
                  onClose();
                } finally {
                  setIsAccepting(false);
                }
              }}
              className="temporary-event-menu__button-group__button"
            >
              {t('calendar.common.accept')}
            </LoadingButton>
          </div>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TemporaryEventMenu;
